// THE GENESIS ERROR CODES SPARK RECOGNISES, IN ONE PLACE.
//
// genesis catalogues its deliberate refusals: a `ServiceException` subclass carries an
// `ErrorMessage` with a code, and genesis's exception resolver copies that code into
// `extensions.code` on the wire. spark matches on those codes to tell a refusal apart from a
// failure, and to answer each one with the repair it actually calls for.
//
// WHY THIS FILE EXISTS. These strings are a contract between two repositories that nothing enforced:
// they were spelled out as literals at each call site, so a code that genesis renumbered or reused
// would go on matching nothing — or worse, matching the wrong refusal — with no test failing and no
// type breaking, because to TypeScript they are just strings (jfalkenstein, !238 note_25773).
// Collecting them here means a reader can see the whole cross-repo surface at once, and gives
// genesis's contract test a definite list to be checked against.
//
// HOW IT IS GUARDED. The real guard is in genesis, not here:
// `GraphQlMessagesPublishedContractTest` pins the id of every message on this list, so renumbering
// one fails in the repository doing the renumbering — before it can reach spark at all. A guard on
// this side could only notice after somebody re-synced the schema, which is too late to be useful.
//
// What it cannot do: catch genesis giving the same id a different MEANING. Nothing mechanical can.
// That is why each entry below states what the refusal means rather than only naming it — a reviewer
// comparing the two sides has something to compare.
//
// ADDING ONE: add it here with what it means and which operation raises it, add the same id to
// genesis's GraphQlMessagesPublishedContractTest, and import the constant. Never inline the string.

/**
 * "This document is still used by a shared item, so it cannot be deleted."
 *
 * Raised by `deleteDocument`. Unusually, its extensions carry data spark reads — `sharedItemIds` —
 * so the page can name what is in the way rather than only that something is.
 */
export const DOCUMENT_USED_IN_SHARED_ITEMS = "GENESIS001218";

/**
 * "This would leave the company with no usable administrator."
 *
 * Raised by `removeCompanyMember` and `setCompanyMemberRole`. Matched by code rather than by
 * classification: it used to be the only VALIDATION those mutations could reach, which stopped
 * being true the moment {@link PERSON_CANNOT_ADMINISTER_COMPANY} appeared on the same two.
 */
export const LAST_COMPANY_ADMIN = "GENESIS001240";

/**
 * "That would be one contact person too many."
 *
 * Raised by `setCompanyContactPerson`. The cap is CompanyMembershipController.MAX_CONTACT_PERSONS,
 * ten since genesis#268, and company-profiles/0006 §4 was amended to ten in concept v13 — code and
 * concept agree, so the concept's number IS the enforced one.
 *
 * Nothing here spells the number out because nothing here needs it: this module matches on the code
 * alone. The sentence a reader sees is built in use-company-admin.ts from spark's own
 * MAX_CONTACT_PERSONS, which is the SECOND pin of the same number — genesis parameterises its
 * message, but spark never parses the number back out of it. Both pins move together or spark
 * refuses at the old cap and quotes it.
 */
export const TOO_MANY_CONTACT_PERSONS = "GENESIS001241";

/**
 * "That person's account is not active, so they cannot administer a company."
 *
 * MUST stay distinguishable from {@link LAST_COMPANY_ADMIN}: the two ask for opposite repairs. One
 * says promote somebody else before you leave; this one says the person you just picked cannot be
 * promoted at all. Answering either with the other's wording sends the admin round in a circle.
 */
export const PERSON_CANNOT_ADMINISTER_COMPANY = "GENESIS001242";

// GENESIS001244, GENESIS001245 and GENESIS001246 USED TO BE HERE AND ARE BURNT. They belonged to
// the shared competency-field catalogue, which genesis deleted along with the vocabulary itself
// (@external-adr GENESIS-0031-company-expertise-is-per-company-free-text): expertise is now per-company free
// text, so there is no catalogue to clash with, to find in use, or to be refused write access to.
// genesis's GraphQlMessagesPublishedContractTest#retiredIdsAreNotRecycled fails its build if the
// three numbers are ever reissued, so they are not coming back with a different meaning — which is
// why this note is a note and not a constant. In particular 001247 below is NOT 001244 renumbered:
// 001244 said "the platform already has this field, use that one", which is a sentence nothing on
// the platform can say any more.

/**
 * "This company already lists that expertise."
 *
 * Raised by `setCompanyCompetency`, which ADDS rather than upserts. Compared case-insensitively and
 * ignoring surrounding whitespace, and scoped to ONE company: the same label at another company is
 * a different row and no clash at all. The repair is to remove the existing entry first — changing
 * a degree is a remove followed by an add, because that is the only editing model the screen has.
 *
 * Matched by CODE, not by classification: this mutation reaches several VALIDATION refusals
 * (001227 degree, 001228 blank, 001229 over-length), and a loose match would put the duplicate's
 * wording on any of them.
 */
export const COMPANY_EXPERTISE_ALREADY_LISTED = "GENESIS001247";

/**
 * "This company already lists as many expertise entries as genesis allows."
 *
 * Raised by `setCompanyCompetency` when the company already holds
 * `CompanyCompetencyController.MAX_COMPETENCIES` rows. The editor holds its own copy of that number
 * and disables Add before the click, so this is the backstop for the two copies drifting apart or for
 * another admin filling the last slot in a second session — the same arrangement as
 * {@link TOO_MANY_CONTACT_PERSONS}.
 *
 * MUST stay distinguishable from {@link COMPANY_EXPERTISE_LABEL_TOO_LONG}: both are raised by this one
 * mutation and both are about a limit, but one is repaired by shortening the label and the other by
 * removing an entry. Answering either with the other's wording sends the admin to edit a box that is
 * not the problem.
 *
 * It also outranks {@link COMPANY_EXPERTISE_ALREADY_LISTED} on genesis's side: at the cap, re-adding a
 * label the company already lists is refused as full rather than as a duplicate, because "the list is
 * full" is true whatever was sent.
 */
export const COMPANY_EXPERTISE_LIST_FULL = "GENESIS001249";

/**
 * "This company is inactive — nothing can be published in its name, and its content is frozen."
 *
 * Raised by `createSharedItem` when the named company is INACTIVE, and by `updateSharedItem`,
 * `deleteSharedItem`, `signUpSharedProject`, `rateSharedItem` and `viewSharedItem` for an item that
 * company owns. Concept v9: a dormant company's content can be read, and nothing else — see
 * `company-profiles/0002-company-account-model` §6.
 *
 * <b>It must not be collapsed into the generic authorisation refusal, and that is the whole reason
 * it has its own code.</b> The two are repaired in OPPOSITE directions: this one by reactivating the
 * company, the other by being made an admin — and the caller who sees this one already IS an admin.
 * Telling them to ask for access would send them to the one place that cannot help.
 */
export const COMPANY_IS_INACTIVE = "GENESIS001250";

/**
 * "That expertise label is longer than genesis stores."
 *
 * Raised by `setCompanyCompetency` when the trimmed label exceeds 255 characters. The editor holds
 * its own copy of that cap and disables Add before the click, so this is the backstop for the two
 * copies drifting apart — the same arrangement as MAX_CONTACT_PERSONS, and for the same reason:
 * genesis is the authority and a client-side cap that is merely out of date must still produce an
 * actionable sentence rather than "something went wrong".
 *
 * Its siblings are deliberately NOT listed. GENESIS001228 ("label must not be empty") and
 * GENESIS001227 ("degree must be between 0 and 100") are unreachable by construction rather than by
 * a copied constant: a blank Add is disabled on the trimmed value the click would send, and every
 * degree comes from the shared seven-level scale, whose steps are percentages. Neither depends on a
 * number that can drift, so neither needs a backstop.
 */
export const COMPANY_EXPERTISE_LABEL_TOO_LONG = "GENESIS001229";

/**
 * Every code above. Kept beside the constants deliberately: this list is what a reviewer diffs
 * against genesis's published-contract test, and a code added without an entry here is a code that
 * quietly leaves the contract.
 */
export const ALL_GENESIS_ERROR_CODES = [
  DOCUMENT_USED_IN_SHARED_ITEMS,
  LAST_COMPANY_ADMIN,
  TOO_MANY_CONTACT_PERSONS,
  PERSON_CANNOT_ADMINISTER_COMPANY,
  COMPANY_EXPERTISE_ALREADY_LISTED,
  COMPANY_EXPERTISE_LIST_FULL,
  COMPANY_EXPERTISE_LABEL_TOO_LONG,
  COMPANY_IS_INACTIVE,
] as const;
